import type { ParentComponent, Resource, VoidComponent } from "solid-js";
import { createResource, Match, Show, Switch } from "solid-js";
import type { ExtendableInputProps } from "./Input";
import Input from "./Input";
import { isEmpty, isNil } from "lodash-es";
import { TextAndIconButton } from "../button/Button";
import { useTranslation } from "../../state/i18n";
import { Icon } from "../icon/Icon";
import InlineMessage from "../inlineMessage/InlineMessage";
import { SingleSelect } from "../menu/Select";
import type { ServiceAccount, ServiceAccountType } from "../../types/ServiceAccount";
import ServiceAccountService from "../../services/ServiceAccountService";
import ServiceAccountId from "../../state/ServiceAccountId";
import GenericFailureModal from "../../pages/GenericFailureModal";
import { useModalManager } from "../modal/ModalManager";

type ServiceAccountSelectInputProps = {
  type: ServiceAccountType;
  fetchServiceAccounts: () => Promise<ServiceAccount[]>;
  value: ServiceAccountId | null;
  onChange: (serviceAccountId: ServiceAccountId) => void;
} & ExtendableInputProps;

const ResourceState: ParentComponent<{ resource: Resource<unknown> }> = (props) => {
  const { t } = useTranslation();

  return (
    <Switch>
      <Match when={props.resource.state === "pending" || props.resource.state === "unresolved"}>
        <div class="flex justify-center py-4">
          <Icon name="spinner" size="xl" type="solid" class="animate-spin icon-secondary" />
        </div>
      </Match>
      <Match when={props.resource.state === "errored"}>
        <InlineMessage variant="danger" label={t(($) => $.SERVICE_ACCOUNT_SELECT_INPUT_FETCH_FAILED)} />
      </Match>
      <Match when={props.resource.state === "refreshing" || props.resource.state === "ready"}>{props.children}</Match>
    </Switch>
  );
};

const ServiceAccountSelectInput: VoidComponent<ServiceAccountSelectInputProps> = (props) => {
  const { t } = useTranslation();
  const { open } = useModalManager();

  const [resource, { refetch }] = createResource<{ variant: "text"; value: string; label: string }[]>(async () => {
    const serviceAccounts = await props.fetchServiceAccounts();
    return serviceAccounts
      .filter((serviceAccount) => serviceAccount.type === props.type)
      .map((serviceAccount) => ({
        variant: "text",
        value: serviceAccount.id.toString(),
        label: serviceAccount.name ?? "",
      }));
  });

  return (
    <Input
      required={props.required}
      errorMessage={resource.state === "refreshing" || resource.state === "ready" ? props.errorMessage : undefined}
      info={props.info}
      label={props.label}
    >
      {() => (
        <ResourceState resource={resource}>
          <Show when={!isEmpty(resource()!)}>
            <SingleSelect
              hasError={!isNil(props.errorMessage)}
              options={resource()!}
              onSelect={(value) => props.onChange(ServiceAccountId.from(value))}
              ariaLabel={props.label}
              value={props.value?.toString()}
              placeholder={t(($) => $.SERVICE_ACCOUNT_SELECT_INPUT_PLACEHOLDER)}
              sameWidth
            />

            <span class="text-center text-header-sm text-secondary uppercase">
              {t(($) => $.SERVICE_ACCOUNT_SELECT_INPUT_OR)}
            </span>
          </Show>

          <TextAndIconButton
            text={t(($) => $.SERVICE_ACCOUNT_SELECT_INPUT_CONNECT_NEW)}
            icon="link"
            variant="secondary"
            onClick={async () => {
              try {
                const serviceAccountId = await ServiceAccountService.connectServiceAccount(props.type);
                if (!isNil(serviceAccountId)) {
                  refetch();
                  props.onChange(serviceAccountId);
                }
              } catch (err) {
                open({
                  content: () => <GenericFailureModal error={err} />,
                });
              }
            }}
          />
        </ResourceState>
      )}
    </Input>
  );
};

export default ServiceAccountSelectInput;
